const THEME_COLOR_STORAGE_KEY = 'portfolio-theme-color-overrides-v2'
const THEME_PAINT_STORAGE_KEY = 'portfolio-theme-paint-v1'

export const THEME_COLORS_EVENT = 'theme-colors-changed'

export const DEFAULT_THEME_MASTER = {
  hue: 0,
  saturation: 100,
  brightness: 100,
}

const isMasterActive = (master = DEFAULT_THEME_MASTER) =>
  (master.hue || 0) !== 0
  || (master.saturation ?? 100) !== 100
  || (master.brightness ?? 100) !== 100

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const isLegacyColorOverrides = (saved) =>
  Boolean(saved && typeof saved === 'object' && ('bgPrimary' in saved || 'accentBorder' in saved))

const normalizeThemeColorSave = (saved) => {
  if (!saved || typeof saved !== 'object') {
    return { master: { ...DEFAULT_THEME_MASTER }, overrides: {} }
  }
  if (isLegacyColorOverrides(saved)) {
    // Older flat saves pinned every swatch and blocked master controls.
    return { master: { ...DEFAULT_THEME_MASTER }, overrides: {} }
  }
  return {
    master: { ...DEFAULT_THEME_MASTER, ...(saved.master || {}) },
    overrides: saved.overrides && typeof saved.overrides === 'object' ? { ...saved.overrides } : {},
  }
}

export const THEME_COLOR_GROUPS = [
  {
    id: 'panels',
    label: 'Panels',
    fields: [
      { key: 'bgPrimary', var: '--color-bg-primary', label: 'Primary' },
      { key: 'bgSecondary', var: '--color-bg-secondary', label: 'Secondary' },
      { key: 'bgTertiary', var: '--color-bg-tertiary', label: 'Tertiary' },
    ],
  },
  {
    id: 'text',
    label: 'Text',
    fields: [
      { key: 'textPrimary', var: '--color-text-primary', label: 'Primary' },
      { key: 'textSecondary', var: '--color-text-secondary', label: 'Secondary' },
      { key: 'textMuted', var: '--color-text-muted', label: 'Muted' },
      { key: 'chromeDisabled', var: '--color-chrome-disabled', label: 'Disabled' },
      { key: 'projectTopicHeading', var: '--color-project-topic-heading', label: 'Topic headings' },
      { key: 'projectBodyText', var: '--color-project-body-text', label: 'Project body' },
      { key: 'hubHeading', var: '--color-hub-heading', label: 'Hub heading' },
      { key: 'hubBody', var: '--color-hub-body', label: 'Hub body' },
    ],
  },
  {
    id: 'selection',
    label: 'Selection',
    fields: [
      { key: 'accentBorder', var: '--color-accent-border', label: 'Accent' },
      { key: 'selectionBg', var: '--color-maya-selection-bg', label: 'Fill' },
      { key: 'selectionBorder', var: '--color-maya-selection-border', label: 'Border' },
      { key: 'scrollbarThumb', var: '--color-scrollbar-thumb', label: 'Scrollbar' },
    ],
  },
  {
    id: 'chrome',
    label: 'Chrome',
    fields: [
      { key: 'border', var: '--color-border', label: 'Border' },
      { key: 'mayaHighlight', var: '--color-maya-highlight', label: 'Highlight' },
      { key: 'scrollbarTrack', var: '--color-scrollbar-track', label: 'Scroll track' },
      { key: 'playerBarBg', var: '--color-player-bar-bg', label: 'Player bar' },
      { key: 'playerSurfaceBg', var: '--color-player-surface-bg', label: 'Player surface' },
      { key: 'playerTimelineBg', var: '--color-player-timeline-bg', label: 'Timeline interior' },
      { key: 'playerTimelineTick', var: '--color-player-timeline-tick', label: 'Timeline ticks' },
      { key: 'playerPlayhead', var: '--color-player-playhead', label: 'Playhead' },
    ],
  },
]

const THEME_COLOR_FIELDS = THEME_COLOR_GROUPS.flatMap((group) => group.fields)

const THEME_COLOR_VAR_NAMES = THEME_COLOR_FIELDS.map((field) => field.var)

const cssColorToHex = (raw) => {
  if (!raw) return '#000000'
  const trimmed = raw.trim()
  if (/^#[0-9a-fA-F]{3,8}$/.test(trimmed)) {
    if (trimmed.length === 4) {
      return `#${trimmed[1]}${trimmed[1]}${trimmed[2]}${trimmed[2]}${trimmed[3]}${trimmed[3]}`.toLowerCase()
    }
    return trimmed.slice(0, 7).toLowerCase()
  }
  const rgb = trimmed.match(/^rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)/)
  if (!rgb) return '#000000'
  const hex = (value) => Math.round(Number(value)).toString(16).padStart(2, '0')
  return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`
}

const readComputedThemeColors = () => {
  const styles = getComputedStyle(document.documentElement)
  const colors = {}
  THEME_COLOR_FIELDS.forEach((field) => {
    colors[field.key] = cssColorToHex(styles.getPropertyValue(field.var))
  })
  return colors
}

const readComputedThemePaintVars = () => {
  const styles = getComputedStyle(document.documentElement)
  const vars = {}
  THEME_COLOR_FIELDS.forEach((field) => {
    const value = styles.getPropertyValue(field.var).trim()
    if (value) vars[field.var] = value
  })
  return vars
}

const writeThemePaintSnapshot = (themeId) => {
  if (typeof window === 'undefined' || !themeId) return

  try {
    window.localStorage.setItem(
      THEME_PAINT_STORAGE_KEY,
      JSON.stringify({
        theme: themeId,
        vars: readComputedThemePaintVars(),
      }),
    )
  } catch {
    // Ignore quota / private-mode failures
  }
}

const readAllThemeColorOverrides = () => {
  try {
    const raw = window.localStorage.getItem(THEME_COLOR_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export const readThemeColorOverrides = (themeId) => {
  const all = readAllThemeColorOverrides()
  const saved = all[themeId]
  if (!saved || typeof saved !== 'object') return null
  return normalizeThemeColorSave(saved)
}

export const writeThemeColorOverrides = (themeId, payload) => {
  const all = readAllThemeColorOverrides()
  if (!payload) {
    delete all[themeId]
  } else {
    all[themeId] = payload
  }
  try {
    if (Object.keys(all).length) {
      window.localStorage.setItem(THEME_COLOR_STORAGE_KEY, JSON.stringify(all))
    } else {
      window.localStorage.removeItem(THEME_COLOR_STORAGE_KEY)
    }
  } catch {
    // Ignore quota / private-mode failures
  }
}

export const readThemeBaselineColors = () => {
  clearThemeColorInlineOverrides()
  // Read after clearing inline overrides so baseline always comes from CSS theme tokens.
  return readComputedThemeColors()
}

const hexToHsl = (hex) => {
  const normalized = cssColorToHex(hex)
  const r = parseInt(normalized.slice(1, 3), 16) / 255
  const g = parseInt(normalized.slice(3, 5), 16) / 255
  const b = parseInt(normalized.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      default:
        h = ((r - g) / d + 4) / 6
        break
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

const hslToHex = (h, s, l) => {
  const hue = ((h % 360) + 360) % 360
  const sat = clamp(s, 0, 100) / 100
  const light = clamp(l, 0, 100) / 100

  if (sat === 0) {
    const gray = Math.round(light * 255)
      .toString(16)
      .padStart(2, '0')
    return `#${gray}${gray}${gray}`
  }

  const hueToRgb = (p, q, t) => {
    let value = t
    if (value < 0) value += 1
    if (value > 1) value -= 1
    if (value < 1 / 6) return p + (q - p) * 6 * value
    if (value < 1 / 2) return q
    if (value < 2 / 3) return p + (q - p) * (2 / 3 - value) * 6
    return p
  }

  const q = light < 0.5 ? light * (1 + sat) : light + sat - light * sat
  const p = 2 * light - q
  const hk = hue / 360
  const r = hueToRgb(p, q, hk + 1 / 3)
  const g = hueToRgb(p, q, hk)
  const b = hueToRgb(p, q, hk - 1 / 3)
  const toHex = (channel) => Math.round(channel * 255).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

const adjustHexWithMaster = (hex, master = DEFAULT_THEME_MASTER) => {
  const { h, s, l } = hexToHsl(hex)
  const hueShift = master.hue || 0
  const satMult = (master.saturation ?? 100) / 100
  const light = clamp(l + ((master.brightness ?? 100) - 100) * 0.45, 0, 100)

  // Every colour rotates by the same amount; near-greys also get a saturation
  // floor that ramps with the shift, so they take on tint gradually.
  const hue = h + hueShift
  const saturated =
    Math.abs(hueShift) > 0 && s < 20
      ? Math.max(s * satMult, Math.min(28, Math.abs(hueShift) / 4))
      : s * satMult
  return hslToHex(hue, clamp(saturated, 0, 100), light)
}

export const snapshotThemeForSave = (values = {}) => {
  const overrides = {}
  THEME_COLOR_FIELDS.forEach((field) => {
    const value = values[field.key]
    if (value) overrides[field.key] = cssColorToHex(value)
  })
  return {
    master: { ...DEFAULT_THEME_MASTER },
    overrides,
  }
}

export const bakeActiveMasterState = (
  baseColors,
  master = DEFAULT_THEME_MASTER,
  overrides = {},
  composedValues = null,
) => {
  if (!isMasterActive(master)) {
    return {
      master: { ...DEFAULT_THEME_MASTER, ...master },
      overrides: { ...overrides },
    }
  }

  const resolved =
    composedValues || buildThemeColors(baseColors, master, overrides)

  const bakedOverrides = { ...overrides }
  THEME_COLOR_FIELDS.forEach((field) => {
    const value = resolved[field.key]
    if (value) bakedOverrides[field.key] = cssColorToHex(value)
  })

  return {
    master: { ...DEFAULT_THEME_MASTER },
    overrides: bakedOverrides,
  }
}

export const buildThemeColors = (
  referenceColors = {},
  master = DEFAULT_THEME_MASTER,
  overrides = {},
) => {
  const masterActive = isMasterActive(master)
  const result = {}
  THEME_COLOR_FIELDS.forEach((field) => {
    const pinned = overrides[field.key]
    if (pinned) {
      result[field.key] = cssColorToHex(pinned)
      return
    }

    const reference = cssColorToHex(referenceColors[field.key] || '#000000')
    if (!masterActive) {
      result[field.key] = reference
      return
    }

    result[field.key] = adjustHexWithMaster(reference, master)
  })
  return result
}

const clearThemeColorInlineOverrides = () => {
  const root = document.documentElement
  THEME_COLOR_VAR_NAMES.forEach((name) => root.style.removeProperty(name))
  root.style.removeProperty('--maya-panel-chrome')
  root.style.removeProperty('--maya-panel-menu-gutter')
  root.style.removeProperty('--maya-panel-row-active-bg')
  root.style.removeProperty('--maya-panel-tool-active')
}

export const applyThemeColorOverrides = (colors = {}) => {
  const root = document.documentElement
  THEME_COLOR_FIELDS.forEach((field) => {
    const value = colors[field.key]
    if (value) root.style.setProperty(field.var, value)
    else root.style.removeProperty(field.var)
  })
  // Derived Maya panel tokens read from the base color vars above.
  root.style.setProperty('--maya-panel-chrome', 'var(--color-bg-tertiary)')
  root.style.setProperty('--maya-panel-row-active-bg', 'var(--color-maya-selection-bg)')
  root.style.setProperty('--maya-panel-tool-active', 'var(--color-maya-selection-bg)')
}

export const emitThemeColorsChanged = () => {
  window.dispatchEvent(new CustomEvent(THEME_COLORS_EVENT))
}

export const buildReferencePalette = (factoryBase, overrides = {}) => {
  const reference = { ...factoryBase }
  THEME_COLOR_FIELDS.forEach((field) => {
    if (overrides[field.key]) {
      reference[field.key] = cssColorToHex(overrides[field.key])
    }
  })
  return reference
}

const isFullThemeColorSnapshot = (overrides = {}) =>
  THEME_COLOR_FIELDS.every((field) => Boolean(overrides[field.key]))

export const resolveThemeColorEditingState = (factoryBase, saved) => {
  const factory = { ...factoryBase }

  if (!saved) {
    return {
      master: { ...DEFAULT_THEME_MASTER },
      overrides: {},
      reference: factory,
    }
  }

  const normalized = normalizeThemeColorSave(saved)
  const master = { ...DEFAULT_THEME_MASTER, ...normalized.master }
  const savedOverrides = normalized.overrides

  if (isFullThemeColorSnapshot(savedOverrides)) {
    return {
      master,
      overrides: {},
      reference: buildReferencePalette(factory, savedOverrides),
    }
  }

  const reference = buildReferencePalette(factory, savedOverrides)
  return {
    master,
    overrides: { ...savedOverrides },
    reference,
  }
}

export const syncThemeColorOverrides = (themeId) => {
  const saved = readThemeColorOverrides(themeId)
  const factoryBase = readThemeBaselineColors()
  const { master, overrides, reference } = resolveThemeColorEditingState(factoryBase, saved)
  const colors = buildThemeColors(reference, master, overrides)
  applyThemeColorOverrides(colors)
  emitThemeColorsChanged()
  writeThemePaintSnapshot(themeId)
}

export const colorsToCssBlock = (themeId, colors) => {
  const lines = THEME_COLOR_FIELDS
    .map((field) => {
      const value = colors[field.key]
      if (!value) return null
      return `  ${field.var}: ${value};`
    })
    .filter(Boolean)
  return `[data-theme='${themeId}'] {\n\n${lines.join('\n\n')}\n\n}`
}
